import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useMessageBus } from "@jti/fe-message-bus";
import type { Product as CartItem } from "@jti/magento";
import { ICartItemErrorType, usePdpLink } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { useTranslations } from "@jti/ui";

import { ProductInfoAndActions } from "@checkout/components/CartItem/ProductInfoAndActions/ProductInfoAndActions";
import { usePromotionalOffers } from "@checkout/hooks";
import { hasCartErrorCode } from "@checkout/utils/getCartErrors";

import { computeQuantityOrderLimit } from "../utils/computeQuantityOrderLimit";

export type CompactItemProps = {
  item: CartItem;
  blockQuantityAdd?: boolean;
  isRemovable?: boolean;
  onRemoveClick(id: string | undefined): void;
  isQuantityInputVisible?: boolean;
  onQuantityChange(id: string | undefined, quantity: number): void;
};

export const CompactItem: FC<CompactItemProps> = ({
  item,
  onRemoveClick,
  onQuantityChange,
  blockQuantityAdd = false,
  isRemovable = true,
  isQuantityInputVisible = true,
}) => {
  const {
    id,
    sku,
    imageUrl,
    name,
    quantity,
    price,
    status,
    enable_order_quantity_limit,
    order_quantity_limit,
    promotionalOffers,
    subscription = 0,
    errors,
    tradeIn,
    has_registration_consent,
    image,
    nicotineStrength,
  } = item;

  const messageBus = useMessageBus();
  const { __ } = useTranslations();
  const handleQuantityChange = useCallback((value: number) => onQuantityChange(id, value), [id, onQuantityChange]);
  const [limitMessageStatus, setLimitMessageStatus] = useState(false);
  const [isQuantityLimitReached, setIsQuantityLimitReached] = useState(false);
  const { SHOP_URL, PDP_URL } = useConfig();
  const pdpLink = usePdpLink(sku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });
  const {
    isLimitReached: isPromotionalOfferLimitReached,
    hasPromotionalOffers,
    promotionalOfferLimit,
  } = usePromotionalOffers(quantity, promotionalOffers);
  const grandTotalPrice = useSelector((state) => state.cart.prices.data?.grandTotal.value) ?? 0;
  const shippingPrice = useSelector((state) => state.cart.prices.data?.shipping?.value) ?? 0;
  const maxOrderLimit = useSelector((state) => state.cart.storeConfig?.order_limit_value);
  const orderLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const isSubscription = subscription === 1;
  const isOutOfStock = (errors && hasCartErrorCode(errors, ICartItemErrorType.ItemQty)) || status === "OUT_OF_STOCK";

  const quantityOrderLimit = computeQuantityOrderLimit({
    hasPromotionalOffers,
    promotionalOfferLimit,
    tradeIn,
    enable_order_quantity_limit,
    order_quantity_limit,
  });

  const handleRemoveClick = useCallback(() => {
    onRemoveClick(id);

    messageBus.publish("cart.limit.update", {
      payload: {
        sku,
        quantity: 0,
        limit: quantityOrderLimit.quantityLimit,
        quantityLimitReached: false,
      },
    });
  }, [id, sku, quantityOrderLimit.quantityLimit, onRemoveClick]);

  useEffect(() => {
    setLimitMessageStatus(grandTotalPrice + price.value / quantity - shippingPrice > maxOrderLimit);
  }, [grandTotalPrice]);

  useEffect(() => {
    const quantityLimitReached = quantity >= order_quantity_limit && enable_order_quantity_limit === 1;
    const specialOfferLimitReached = hasPromotionalOffers && isPromotionalOfferLimitReached;

    setIsQuantityLimitReached(quantityLimitReached || specialOfferLimitReached);

    messageBus.publish("cart.limit.update", {
      payload: {
        sku,
        quantity,
        limit: quantityOrderLimit.quantityLimit,
        quantityLimitReached: quantityLimitReached || specialOfferLimitReached,
      },
    });
  }, [quantity]);

  const isAddDisabled = blockQuantityAdd || isQuantityLimitReached;

  return (
    <ProductInfoAndActions
      url={pdpLink}
      name={name}
      imageUrl={imageUrl || image?.url || ""}
      outOfStock={isOutOfStock}
      price={price}
      hasRegistrationConsent={has_registration_consent}
      isQuantityInputVisible={isQuantityInputVisible}
      isRemovable={isRemovable}
      quantity={{
        value: quantity,
        disabledAdd: isAddDisabled,
        max: quantityOrderLimit.isLimit === 1 ? quantityOrderLimit.quantityLimit : undefined,
        disabled: isOutOfStock,
        title: isOutOfStock ? __("cart.cart_item.out_of_stock") : undefined,
        onChange: handleQuantityChange,
        orderLimitEnabled,
        isOrderLimitReached: limitMessageStatus,
        maxOrderLimit,
        currency: price.currency,
      }}
      removeItemHandler={handleRemoveClick}
      isQuantityLimitReached={isQuantityLimitReached}
      quantityLimit={quantityOrderLimit.quantityLimit}
      isSubscription={isSubscription}
      nicotineStrength={nicotineStrength}
    />
  );
};
